import type {
  ExtendedTaskDef as OpenApiExtendedTaskDef,
  SignalResponse as OpenApiSignalResponse,
  Task,
  ExtendedConductorApplication as OpenApiExtendedConductorApplication,
  Tag,
  ConductorUser as OpenApiConductorUser,
  Group as OpenApiGroup,
  IntegrationApiUpdate as OpenApiIntegrationApiUpdate,
  RateLimitConfig as OpenApiRateLimitConfig,
  BulkResponse as OpenApiBulkResponse,
  TaskSummary as OpenApiTaskSummary,
} from "./generated";

export interface CommonTaskDef {
  name: string;
  taskReferenceName: string;
}

export enum Consistency {
  SYNCHRONOUS = "SYNCHRONOUS",
  DURABLE = "DURABLE",
  REGION_DURABLE = "REGION_DURABLE",
}

export enum ReturnStrategy {
  TARGET_WORKFLOW = "TARGET_WORKFLOW",
  BLOCKING_WORKFLOW = "BLOCKING_WORKFLOW",
  BLOCKING_TASK = "BLOCKING_TASK",
  BLOCKING_TASK_INPUT = "BLOCKING_TASK_INPUT",
}

export enum TaskResultStatusEnum {
  IN_PROGRESS = "IN_PROGRESS",
  FAILED = "FAILED",
  FAILED_WITH_TERMINAL_ERROR = "FAILED_WITH_TERMINAL_ERROR",
  COMPLETED = "COMPLETED",
}

export enum TaskType {
  START = "START",
  SIMPLE = "SIMPLE",
  DYNAMIC = "DYNAMIC",
  FORK_JOIN = "FORK_JOIN",
  FORK_JOIN_DYNAMIC = "FORK_JOIN_DYNAMIC",
  DECISION = "DECISION",
  SWITCH = "SWITCH",
  JOIN = "JOIN",
  DO_WHILE = "DO_WHILE",
  SUB_WORKFLOW = "SUB_WORKFLOW",
  START_WORKFLOW = "START_WORKFLOW",
  EVENT = "EVENT",
  WAIT = "WAIT",
  WAIT_FOR_WEBHOOK = "WAIT_FOR_WEBHOOK",
  USER_DEFINED = "USER_DEFINED",
  HTTP = "HTTP",
  HTTP_POLL = "HTTP_POLL",
  HUMAN = "HUMAN",
  LAMBDA = "LAMBDA",
  INLINE = "INLINE",
  EXCLUSIVE_JOIN = "EXCLUSIVE_JOIN",
  TERMINAL = "TERMINAL",
  TERMINATE = "TERMINATE",
  KAFKA_PUBLISH = "KAFKA_PUBLISH",
  JSON_JQ_TRANSFORM = "JSON_JQ_TRANSFORM",
  SET_VARIABLE = "SET_VARIABLE",
  GET_DOCUMENT = "GET_DOCUMENT",
  LLM_CHAT_COMPLETE = "LLM_CHAT_COMPLETE",
  LLM_TEXT_COMPLETE = "LLM_TEXT_COMPLETE",
  LLM_GENERATE_EMBEDDINGS = "LLM_GENERATE_EMBEDDINGS",
  LLM_INDEX_TEXT = "LLM_INDEX_TEXT",
  LLM_SEARCH_INDEX = "LLM_SEARCH_INDEX",
  LLM_SEARCH_EMBEDDINGS = "LLM_SEARCH_EMBEDDINGS",
  LLM_STORE_EMBEDDINGS = "LLM_STORE_EMBEDDINGS",
  LLM_GET_EMBEDDINGS = "LLM_GET_EMBEDDINGS",
  GENERATE_IMAGE = "GENERATE_IMAGE",
  GENERATE_AUDIO = "GENERATE_AUDIO",
  CALL_MCP_TOOL = "CALL_MCP_TOOL",
  LIST_MCP_TOOLS = "LIST_MCP_TOOLS",
  PULL_WORKFLOW_MESSAGES = "PULL_WORKFLOW_MESSAGES",
}

export enum ServiceType {
  HTTP = "HTTP",
  MCP_REMOTE = "MCP_REMOTE",
  gRPC = "gRPC",
}

export type TaskDefTypes =
  | SimpleTaskDef
  | DoWhileTaskDef
  | EventTaskDef
  | ForkJoinTaskDef
  | ForkJoinDynamicDef
  | HttpTaskDef
  | InlineTaskDef
  | JsonJQTransformTaskDef
  | KafkaPublishTaskDef
  | SetVariableTaskDef
  | SubWorkflowTaskDef
  | SwitchTaskDef
  | TerminateTaskDef
  | JoinTaskDef
  | WaitTaskDef
  | PullWorkflowMessagesTaskDef;

export interface DoWhileTaskDef extends CommonTaskDef {
  inputParameters: Record<string, unknown>;
  type: TaskType.DO_WHILE;
  startDelay?: number;
  optional?: boolean;
  asyncComplete?: boolean;
  loopCondition: string;
  loopOver: TaskDefTypes[];
}

export interface EventTaskDef extends CommonTaskDef {
  type: TaskType.EVENT;
  sink: string;
  asyncComplete?: boolean;
  optional?: boolean;
}

export interface ForkJoinTaskDef extends CommonTaskDef {
  type: TaskType.FORK_JOIN;
  inputParameters?: Record<string, string>;
  forkTasks: TaskDefTypes[][];
}

export interface JoinTaskDef extends CommonTaskDef {
  type: TaskType.JOIN;
  inputParameters?: Record<string, string>;
  joinOn: string[];
  optional?: boolean;
  asyncComplete?: boolean;
}

export interface ForkJoinDynamicDef extends CommonTaskDef {
  inputParameters: {
    dynamicTasks: TaskDefTypes[] | string;
    dynamicTasksInput: Record<string, unknown> | string;
  };
  type: TaskType.FORK_JOIN_DYNAMIC;
  dynamicForkTasksParam: string; // not string "dynamicTasks",
  dynamicForkTasksInputParamName: string; // not string "dynamicTasksInput",
  startDelay?: number;
  optional?: boolean;
  asyncComplete?: boolean;
}
export interface HttpInputParameters {
  uri: string;
  method: "GET" | "PUT" | "POST" | "DELETE" | "OPTIONS" | "HEAD";
  accept?: string;
  contentType?: string;
  headers?: Record<string, string>;
  body?: unknown;
  connectionTimeOut?: number;
  readTimeOut?: string;
}

export interface HttpTaskDef extends CommonTaskDef {
  inputParameters: {
    [x: string]: unknown;
    http_request: HttpInputParameters;
  };
  type: TaskType.HTTP;
  asyncComplete?: boolean;
  optional?: boolean;
}

export interface InlineTaskInputParameters {
  evaluatorType: "javascript" | "graaljs";
  expression: string;
  [x: string]: unknown;
}

export interface InlineTaskDef extends CommonTaskDef {
  type: TaskType.INLINE;
  inputParameters: InlineTaskInputParameters;
  optional?: boolean;
}

interface ContainingQueryExpression {
  queryExpression: string;
  [x: string | number | symbol]: unknown;
}

export interface JsonJQTransformTaskDef extends CommonTaskDef {
  type: TaskType.JSON_JQ_TRANSFORM;
  inputParameters: ContainingQueryExpression;
  optional?: boolean;
}

export interface KafkaPublishInputParameters {
  topic: string;
  value: string;
  bootStrapServers: string;
  headers: Record<string, string>;
  key: string;
  keySerializer: string;
}

export interface KafkaPublishTaskDef extends CommonTaskDef {
  inputParameters: {
    kafka_request: KafkaPublishInputParameters;
  };
  type: TaskType.KAFKA_PUBLISH;
  optional?: boolean;
}

export interface SetVariableTaskDef extends CommonTaskDef {
  type: TaskType.SET_VARIABLE;
  inputParameters: Record<string, unknown>;
  optional?: boolean;
}

export interface SimpleTaskDef extends CommonTaskDef {
  type: TaskType.SIMPLE;
  inputParameters?: Record<string, unknown>;
  optional?: boolean;
}

export interface SubWorkflowTaskDef extends CommonTaskDef {
  type: TaskType.SUB_WORKFLOW;
  inputParameters?: Record<string, unknown>;
  subWorkflowParam: {
    name: string;
    version?: number;
    taskToDomain?: Record<string, string>;
  };
  optional?: boolean;
}

export interface SwitchTaskDef extends CommonTaskDef {
  inputParameters: Record<string, unknown>;
  type: TaskType.SWITCH;
  decisionCases: Record<string, TaskDefTypes[]>;
  defaultCase: TaskDefTypes[];
  evaluatorType: "value-param" | "javascript";
  expression: string;
  optional?: boolean;
}

export interface TerminateTaskDef extends CommonTaskDef {
  inputParameters: {
    terminationStatus: "COMPLETED" | "FAILED";
    workflowOutput?: Record<string, string>;
    terminationReason?: string;
  };
  type: TaskType.TERMINATE;
  startDelay?: number;
}

export interface WaitTaskDef extends CommonTaskDef {
  type: TaskType.WAIT;
  inputParameters: {
    duration?: string;
    until?: string;
  };
  optional?: boolean;
}

export interface PullWorkflowMessagesTaskDef extends CommonTaskDef {
  type: TaskType.PULL_WORKFLOW_MESSAGES;
  inputParameters: {
    batchSize: number;
  };
  optional?: boolean;
}

// TODO: need to remove this once OpenAPI spec is fixed
export interface ExtendedTaskDef
  extends Omit<
    OpenApiExtendedTaskDef,
    "timeoutSeconds" | "totalTimeoutSeconds"
  > {
  totalTimeoutSeconds?: number;
  timeoutSeconds?: number;
}

// TODO: need to remove this once OpenAPI spec is fixed
export interface SignalResponse extends OpenApiSignalResponse {
  // ========== COMMON FIELDS IN ALL RESPONSES ==========
  priority?: number;
  variables?: Record<string, unknown>;

  // ========== FIELDS SPECIFIC TO TARGET_WORKFLOW & BLOCKING_WORKFLOW ==========
  tasks?: Task[];
  createdBy?: string;
  createTime?: number;
  status?: string;
  updateTime?: number;

  // ========== FIELDS SPECIFIC TO BLOCKING_TASK & BLOCKING_TASK_INPUT ==========
  taskType?: string;
  taskId?: string;
  referenceTaskName?: string;
  retryCount?: number;
  taskDefName?: string;
  retriedTaskId?: string;
  workflowType?: string;
  reasonForIncompletion?: string;
}

// TODO: need to remove this once AccessKey type is added to OpenAPI spec
export interface AccessKey {
  id: string;
  secret: string;
}

// TODO: need to remove this once type is added to OpenAPI spec
export interface AccessKeyInfo {
  id: string;
  createdAt: number;
  status: "ACTIVE" | "INACTIVE";
}

// TODO: need to remove this once ApplicationRole type is added to OpenAPI spec
export type ApplicationRole =
  | "ADMIN"
  | "UNRESTRICTED_WORKER"
  | "METADATA_MANAGER"
  | "WORKFLOW_MANAGER"
  | "APPLICATION_MANAGER"
  | "USER"
  | "USER_READ_ONLY"
  | "WORKER"
  | "APPLICATION_CREATOR"
  | "METADATA_API"
  | "PROMPT_MANAGER";

// TODO: need to remove this once ExtendedConductorApplication type is corrected in OpenAPI spec
export interface ExtendedConductorApplication
  extends Required<Omit<OpenApiExtendedConductorApplication, "tags">> {
  tags?: Tag[];
}

// TODO: remove these once OpenAPI spec includes these fields

/** Extended ConductorUser with fields present in Python SDK but missing from OpenAPI spec */
export interface ExtendedConductorUser extends OpenApiConductorUser {
  contactInformation?: Record<string, string>;
  namespace?: string;
}

/** Extended Group with fields present in Python SDK but missing from OpenAPI spec */
export interface ExtendedGroup extends OpenApiGroup {
  contactInformation?: Record<string, string>;
}

/** Extended IntegrationApiUpdate with fields present in Python SDK but missing from OpenAPI spec */
export interface ExtendedIntegrationApiUpdate
  extends OpenApiIntegrationApiUpdate {
  maxTokens?: number;
  frequency?: string;
}

/** Extended RateLimitConfig with fields present in Python SDK but missing from OpenAPI spec */
export interface ExtendedRateLimitConfig extends OpenApiRateLimitConfig {
  tag?: string;
  concurrentExecutionLimit?: number;
}

/** Extended BulkResponse with fields present in Python SDK but missing from OpenAPI spec */
export interface ExtendedBulkResponse extends OpenApiBulkResponse {
  message?: string;
}

/** Extended TaskSummary with fields present in Python SDK but missing from OpenAPI spec */
export interface ExtendedTaskSummary extends OpenApiTaskSummary {
  domain?: string;
}
